import _ from "lodash";

import { Annotation } from "@com.mgmtp.a12.base/base-model-api/lib/main/header";
import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { findElementByFormModelPath, FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DocumentModel } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { isString } from "./typeGuardUtils";

/**
 * Gibt die Annotations zurück, die am Button hinterlegt sind.
 * @param formModel - Das Form-Modell.
 * @param buttonPath - Der Pfad zum Button.
 */
export function getButtonAnnotations(formModel: FormModel, buttonPath: ModelPath): ReadonlyArray<Annotation> {
	const button = findElementByFormModelPath(formModel, buttonPath);
	if (!button || !FormModel.ButtonType.isInstance(button)) {
		throw new Error("Couldn't find button");
	}

	return button.annotations ?? [];
}

/**
 * Annotations placed directly on a cell (e.g. a {@link FormModel.Control}) are lost when that cell is
 * swapped out via a `modulf_replaceNode` annotation during generation, because the whole node is replaced.
 * As a workaround such annotations can instead be placed on the surrounding {@link FormModel.Row} (which
 * is never replaced), with a value prefixed by `for=cell.<index>, ` to indicate which cell they refer to.
 * This resolves the annotation with the given `annotationName` for the cell at `cellPath` from its parent
 * row, preferring an annotation placed directly on the cell itself over one on the row.
 * @param formModel - Das Form-Modell.
 * @param rowPath - Der Pfad zur umgebenden Row.
 * @param cell - Das Zell-Element, für das die Annotation gesucht wird.
 * @param annotationName - Der Name der gesuchten Annotation.
 */
export function findCellAnnotationValue(
	formModel: FormModel,
	rowPath: ModelPath,
	cell: FormModel.CellType,
	annotationName: string
): string | undefined {
	const ownAnnotationValue = cell.annotations?.find(({ name }) => name === annotationName)?.value;
	if (isString(ownAnnotationValue)) {
		return ownAnnotationValue;
	}

	const row = findElementByFormModelPath(formModel, rowPath);
	if (!row || !FormModel.Row.isInstance(row)) {
		return undefined;
	}
	const cellIndex = row.cell?.findIndex(c => c === cell);
	if (cellIndex === undefined || cellIndex < 0) {
		return undefined;
	}

	const rowAnnotationValue = row.annotations?.find(({ name }) => name === annotationName)?.value;
	if (!isString(rowAnnotationValue)) {
		return undefined;
	}
	const prefix = `for=cell.${cellIndex}, `;
	return rowAnnotationValue.startsWith(prefix) ? rowAnnotationValue.slice(prefix.length) : undefined;
}

/**
 * Finds annotations with a specific key and value in a document model and returns them.
 * @param document the document model in which the annotations are searched.
 * @param targetKey the key of the searched annotation.
 * @param targetValue the value the annotation should have.
 */
export function findAnnotations(
	document: DocumentModel,
	targetKey?: string,
	targetValue?: string
): Map<ModelPath, Record<string, string>> {
	return findGroupAnnotations(
		document.content.modelRoot,
		ModelPath.fromString(document.content.modelRoot.name),
		targetKey,
		targetValue
	);
}

/**
 * Finds annotations with a specific key and value in a group of a document model and returns them.
 * @param group the group in which the annotations are searched.
 * @param groupPath the group path in which the annotations are searched.
 * @param targetKey the key of the searched annotation.
 * @param targetValue the value the annotation should have.
 */
function findGroupAnnotations(
	group: DocumentModel.Group,
	groupPath: ModelPath,
	targetKey?: string,
	targetValue?: string
): Map<ModelPath, Record<string, string>> {
	const annotations = new Map<ModelPath, Record<string, string>>();

	if (group.annotations) {
		const filteredAnnotations = filterAnnotations(group.annotations, targetKey, targetValue);
		if (!_.isEmpty(filteredAnnotations)) {
			annotations.set(groupPath, filteredAnnotations);
		}
	}

	group.elements
		.filter(e => e.type === "Field")
		.forEach(field => {
			const fieldAnnotations = filterAnnotations(field.annotations ?? [], targetKey, targetValue);
			if (!_.isEmpty(fieldAnnotations)) {
				annotations.set([...groupPath, ...ModelPath.fromString(field.name)], fieldAnnotations);
			}
		});

	group.elements
		.filter(e => e.type === "Group")
		.map(group =>
			findGroupAnnotations(
				group as DocumentModel.Group,
				[...groupPath, ...ModelPath.fromString(group.name)],
				targetKey,
				targetValue
			)
		)
		.filter(e => e.size > 0)
		.forEach(e => e.forEach((value, key) => annotations.set(key, value)));

	return annotations;
}

/**
 * Takes annotations and returns the ones with a specific key and value.
 * @param annotations the annotations to be filtered.
 * @param targetKey the key to be filtered for.
 * @param targetValue the value to be filtered for.
 */
function filterAnnotations(
	annotations: Annotation[],
	targetKey?: string,
	targetValue?: string
): Record<string, string> {
	return Object.fromEntries(
		annotations
			.filter(ann => (!targetKey || ann.name === targetKey) && (!targetValue || targetValue === ann.value))
			.map(ann => [ann.name as string, ann.value as string])
	);
}

/**
 * Returns the path found for an id in a document model.
 * @param rootGroup - the root group of the document model.
 * @param id - the searched field id.
 * @param currentPath - the current path.
 */
export const findPathById = (rootGroup: DocumentModel.Group, id: string, currentPath = ""): ModelPath | undefined => {
	for (const child of rootGroup.elements) {
		const childPath = currentPath + "/" + child.name;
		let result;

		if (child.type === "Group") {
			result = findPathById(child, id, childPath);
		} else {
			if (child.id.endsWith(id)) {
				result = childPath;
			}
		}
		if (result) {
			return isString(result) ? ModelPath.fromString(result) : result;
		}
	}
	return undefined;
};

export function modelPathContainsRepetition(documentModel: DocumentModel, modelPath: ModelPath): boolean {
	return pathContainsRepetition(documentModel.content.modelRoot, modelPath);
}
function pathContainsRepetition(group: DocumentModel.Group, path: ModelPath): boolean {
	if (group.repeatability > 1) {
		return true;
	}
	if (path.length <= 1) {
		return false;
	}
	const nextElement = group.elements.find(it => it.name === path[0].elementName);
	if (nextElement?.type !== "Group") {
		return false;
	}
	const remainingPath = path.slice(1, path.length);
	return pathContainsRepetition(nextElement, remainingPath);
}
